import React from 'react';
import { Stack } from '@mui/material';
import { formatCurrency } from 'utils';
import { formatCamelCaseToText } from 'formatters';

import { FieldChange } from './FieldChange';
import { ChangeField, ParsedDecisionEditChanges } from '../../../types';

const formatRate = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
};

const formatAmount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return formatCurrency(value, true);
};

const formatConditions = (value: string[] | null | undefined): string => {
  if (!value || value.length === 0) return '—';
  return value.join('; ');
};

const KNOWN_KEYS: ReadonlySet<string> = new Set([
  'approvalAmount',
  'invoiceAmount',
  'apr',
  'dealerBuyRate',
  'termOptions',
  'paymentTiers',
  'conditions',
]);

const formatGenericValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
  return String(value);
};

interface ParsedDecisionEditRenderProps {
  changes: ParsedDecisionEditChanges;
}

export const ParsedDecisionEditRender = ({ changes }: ParsedDecisionEditRenderProps) => {
  const genericEntries = Object.entries(changes).filter(([key]) => !KNOWN_KEYS.has(key)) as [
    string,
    ChangeField<unknown>,
  ][];

  return (
    <Stack gap="0.25rem">
      {changes.approvalAmount && (
        <FieldChange
          label="Approval Amount"
          before={formatAmount(changes.approvalAmount.before)}
          after={formatAmount(changes.approvalAmount.after)}
        />
      )}

      {changes.invoiceAmount && (
        <FieldChange
          label="Invoice Amount"
          before={formatAmount(changes.invoiceAmount.before)}
          after={formatAmount(changes.invoiceAmount.after)}
        />
      )}

      {changes.apr && (
        <FieldChange label="APR" before={formatRate(changes.apr.before)} after={formatRate(changes.apr.after)} />
      )}

      {changes.dealerBuyRate && (
        <FieldChange
          label="Dealer Buy Rate"
          before={formatRate(changes.dealerBuyRate.before)}
          after={formatRate(changes.dealerBuyRate.after)}
        />
      )}

      {changes.conditions && (
        <FieldChange
          label="Conditions"
          before={formatConditions(changes.conditions.before)}
          after={formatConditions(changes.conditions.after)}
        />
      )}

      {genericEntries.map(([key, field]) => (
        <FieldChange
          key={key}
          label={formatCamelCaseToText(key)}
          before={formatGenericValue(field.before)}
          after={formatGenericValue(field.after)}
        />
      ))}
    </Stack>
  );
};
